import { useState, useEffect } from "react";
import { supabase } from "@/lib/supabase";
import { useQueryClient } from "@tanstack/react-query";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Loader2, Zap, Globe, Check, ChevronsUpDown, Landmark } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";

const COUNTRIES = [
  "Afghanistan", "Albania", "Algeria", "Andorra", "Angola", "Antigua and Barbuda", "Argentina", "Armenia", "Australia", "Austria", "Azerbaijan",
  "Bahamas", "Bahrain", "Bangladesh", "Barbados", "Belarus", "Belgium", "Belize", "Benin", "Bhutan", "Bolivia", "Bosnia and Herzegovina", "Botswana", "Brazil", "Brunei", "Bulgaria", "Burkina Faso", "Burundi",
  "Cabo Verde", "Cambodia", "Cameroon", "Canada", "Central African Republic", "Chad", "Chile", "China", "Colombia", "Comoros", "Congo", "Costa Rica", "Croatia", "Cuba", "Cyprus", "Czech Republic",
  "Denmark", "Djibouti", "Dominica", "Dominican Republic", "Ecuador", "Egypt", "El Salvador", "Equatorial Guinea", "Eritrea", "Estonia", "Eswatini", "Ethiopia",
  "Fiji", "Finland", "France", "Gabon", "Gambia", "Georgia", "Germany", "Ghana", "Greece", "Grenada", "Guatemala", "Guinea", "Guinea-Bissau", "Guyana",
  "Haiti", "Honduras", "Hungary", "Iceland", "India", "Indonesia", "Iran", "Iraq", "Ireland", "Israel", "Italy", "Jamaica", "Japan", "Jordan",
  "Kazakhstan", "Kenya", "Kiribati", "Korea, North", "Korea, South", "Kosovo", "Kuwait", "Kyrgyzstan", "Laos", "Latvia", "Lebanon", "Lesotho", "Liberia", "Libya", "Liechtenstein", "Lithuania", "Luxembourg",
  "Madagascar", "Malawi", "Malaysia", "Maldives", "Mali", "Malta", "Marshall Islands", "Mauritania", "Mauritius", "Mexico", "Micronesia", "Moldova", "Monaco", "Mongolia", "Montenegro", "Morocco", "Mozambique", "Myanmar",
  "Namibia", "Nauru", "Nepal", "Netherlands", "New Zealand", "Nicaragua", "Niger", "Nigeria", "North Macedonia", "Norway", "Oman",
  "Pakistan", "Palau", "Palestine", "Panama", "Papua New Guinea", "Paraguay", "Peru", "Philippines", "Poland", "Portugal", "Qatar",
  "Romania", "Russia", "Rwanda", "Saint Kitts and Nevis", "Saint Lucia", "Saint Vincent", "Samoa", "San Marino", "Sao Tome and Principe", "Saudi Arabia", "Senegal", "Serbia", "Seychelles", "Sierra Leone", "Singapore", "Slovakia", "Slovenia", "Solomon Islands", "Somalia", "South Africa", "South Sudan", "Spain", "Sri Lanka", "Sudan", "Suriname", "Sweden", "Switzerland", "Syria",
  "Taiwan", "Tajikistan", "Tanzania", "Thailand", "Timor-Leste", "Togo", "Tonga", "Trinidad and Tobago", "Tunisia", "Turkey", "Turkmenistan", "Tuvalu", "Uganda", "Ukraine", "United Arab Emirates", "United Kingdom", "United States", "Uruguay", "Uzbekistan",
  "Vanuatu", "Vatican City", "Venezuela", "Vietnam", "Yemen", "Zambia", "Zimbabwe"
];

interface StampFormProps {
  open: boolean;
  onClose: () => void;
  editStamp?: any | null; 
}

export default function StampForm({ open, onClose, editStamp }: StampFormProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [countryOpen, setCountryOpen] = useState(false);

  const [location, setLocation] = useState("");
  const [portOfEntry, setPortOfEntry] = useState("");
  const [type, setType] = useState("country");
  const [stampDate, setStampDate] = useState("");
  const [notes, setNotes] = useState("");
  const [proofUrl, setProofUrl] = useState("");

  useEffect(() => {
    if (open) {
      setLocation(editStamp?.location || "");
      setPortOfEntry(editStamp?.port_of_entry || "");
      setType(editStamp?.type || "country");
      setStampDate(editStamp?.stamp_date || "");
      setNotes(editStamp?.field_notes || ""); 
      setProofUrl(editStamp?.proof_url || "");
    }
  }, [open, editStamp]);

  const handleClose = () => {
    setLocation("");
    setPortOfEntry("");
    setType("country");
    setStampDate("");
    setNotes("");
    setProofUrl("");
    onClose();
  };

  const handlePortChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.toUpperCase().slice(0, 3);
    setPortOfEntry(value);
  };

  const handleSubmit = async () => {
    if (!location.trim()) {
      toast({ title: "Missing Data", description: "Location selection is required.", variant: "destructive" });
      return;
    }

    setIsSubmitting(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Authentication required.");

      const stampData = {
        user_id: user.id,
        location: location.trim(),
        port_of_entry: portOfEntry.trim().toUpperCase() || null, // Explicitly Uppercase
        type,
        stamp_date: stampDate || null,
        field_notes: notes || null,
        proof_url: proofUrl || null,
      };

      // console.log("Attempting to sync:", stampData);

      const { error } = editStamp 
        ? await supabase.from("stamps").update(stampData).eq("id", editStamp.id)
        : await supabase.from("stamps").insert([stampData]);

      if (error) throw error;

      queryClient.invalidateQueries({ queryKey: ["stamps"] });
      toast({ 
        title: "Registry Updated", 
        description: `${location} [${portOfEntry || '---'}] has been updated.` 
      });
      handleClose();
    } catch (error: any) {
      console.error("Supabase Error:", error);
      toast({ title: "Sync Failed", description: error.message, variant: "destructive" });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-md p-0 rounded-none border-black border-2 bg-white shadow-[12px_12px_0px_0px_rgba(0,0,0,1)]">
        <ScrollArea className="max-h-[90vh]">
          <div className="p-8">
            <DialogHeader className="mb-10 border-b border-black/5 pb-6">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-5 h-5 bg-black flex items-center justify-center">
                  <Zap className="w-3 h-3 text-white fill-current" />
                </div>
                <p className="text-[10px] uppercase tracking-[0.4em] text-black/40 font-mono">Registry Entry</p>
              </div>
              <DialogTitle className="font-serif text-4xl italic tracking-tighter">
                {editStamp ? "Amend Stamp" : "New Stamp"}
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-8">
              <div className="grid grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label className="font-serif text-[11px] uppercase tracking-widest text-black/40">Node Type</Label>
                  <Select value={type} onValueChange={(val) => { setType(val); setLocation(""); }}>
                    <SelectTrigger className="rounded-none border-black/10 h-11 font-serif focus:ring-black">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="rounded-none font-serif">
                      <SelectItem value="country">Country</SelectItem>
                      <SelectItem value="city">City</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label className="font-serif text-[11px] uppercase tracking-widest text-black/40">Stamp Date</Label>
                  <Input
                    type="date"
                    className="rounded-none border-black/10 h-11 font-mono text-[11px] uppercase focus-visible:ring-black"
                    value={stampDate}
                    onChange={(e) => setStampDate(e.target.value)}
                  />
                </div>
              </div>

              <div className="space-y-6">
                <div className="space-y-2">
                  <Label className="font-serif text-[11px] uppercase tracking-widest text-black/40">Location Name</Label>
                  {type === "country" ? (
                    <Popover open={countryOpen} onOpenChange={setCountryOpen}>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          role="combobox"
                          className="w-full justify-between rounded-none border-black/10 h-12 font-serif text-left px-4 hover:bg-zinc-50 focus:ring-black"
                        >
                          {location ? location : "Select Country..."}
                          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-20" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0 rounded-none border-black shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]">
                        <Command className="rounded-none">
                          <CommandInput placeholder="Search countries..." className="font-serif h-12" />
                          <CommandList className="max-h-[300px] overflow-y-auto">
                            <CommandEmpty className="py-6 text-center text-xs font-serif italic text-black/40">No country found.</CommandEmpty>
                            <CommandGroup>
                              {COUNTRIES.map((country) => (
                                <CommandItem
                                  key={country}
                                  value={country}
                                  onSelect={(currentValue) => {
                                    setLocation(currentValue);
                                    setCountryOpen(false);
                                  }}
                                  className="font-serif cursor-pointer py-3 aria-selected:bg-black aria-selected:text-white"
                                >
                                  <Check className={cn("mr-2 h-4 w-4", location === country ? "opacity-100" : "opacity-0")} />
                                  {country}
                                </CommandItem>
                              ))}
                            </CommandGroup>
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>
                  ) : (
                    <Input
                      placeholder="Enter city name..."
                      className="rounded-none border-black/10 focus-visible:ring-black font-serif h-12"
                      value={location}
                      onChange={(e) => setLocation(e.target.value)}
                    />
                  )}
                </div>

                <div className="space-y-2">
                  <div className="flex justify-between">
                    <Label className="font-serif text-[11px] uppercase tracking-widest text-black/40">Port of Entry</Label>
                    <span className="text-[10px] font-mono text-black/20">{portOfEntry.length}/3</span>
                  </div>
                  <div className="relative">
                    <Input
                      placeholder="IATA Code (e.g. JFK)"
                      className="rounded-none border-black/10 focus-visible:ring-black font-serif h-12 pl-10 uppercase"
                      value={portOfEntry}
                      onChange={handlePortChange}
                      maxLength={3}
                    />
                    <Landmark className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-black/20" />
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label className="font-serif text-[11px] uppercase tracking-widest text-black/40">Field Notes</Label>
                <Textarea
                  placeholder="Where did you travel from?"
                  className="rounded-none border-black/10 min-h-[100px] font-serif focus-visible:ring-black resize-none p-4"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                />
              </div>

              <div className="space-y-4 pt-6 border-t border-black/5">
                <Label className="font-serif text-[11px] uppercase tracking-widest text-black/40">Proof of Presence</Label>
                <div className="relative">
                  <Input 
                    className="rounded-none border-black/10 font-serif italic h-12 pl-10 text-xs focus-visible:ring-black" 
                    placeholder="Proof URL (e.g. social post, transaction ID)..." 
                    value={proofUrl} 
                    onChange={(e) => setProofUrl(e.target.value)} 
                  />
                  <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-black/20" />
                </div>
              </div>
            </div>

            <DialogFooter className="mt-12 flex flex-col gap-3">
              <Button 
                onClick={handleSubmit} 
                disabled={isSubmitting}
                className="w-full rounded-none bg-black text-white hover:bg-zinc-800 h-14 font-serif text-lg tracking-tight"
              >
                {isSubmitting ? <Loader2 className="w-5 h-5 animate-spin" /> : "Send to Netport"}
              </Button>
              <Button variant="ghost" onClick={handleClose} className="w-full rounded-none font-serif text-[11px] uppercase tracking-widest text-black/30 hover:text-black">
                Discard Entry
              </Button>
            </DialogFooter>
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}